import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import { getMembers, getUrlArrayFromChapter } from "../utils/utils";

import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs";
import Wrapper from "../components/Wrapper/Wrapper";
import ChapterSwitchButtons from "../components/ChapterSwitchButtons/ChapterSwitchButtons";
import Vocabulary from "../components/Vocabulary/Vocabulary";
import VideoTextContainer from "../components/VideoTextContainer/VideoTextContainer";
import { default as TOC } from "../components/ArticleTOC/ArticleTOC";

import classes from "./Articles.module.css";

interface ArticleInterface {
  db: any;
  baselink: string;
  hideButtons?: boolean;
  hideTOC?: boolean;
}

const Article = ({ db, baselink, hideButtons, hideTOC }: ArticleInterface) => {
  const params = useParams();
  const [backToTop, setBackToTop] = useState(false);
  const location = useLocation();
  const [url, setUrl] = useState("");
  const [playing, setPlaying] = useState(false);
  const [urlIndex, setUrlIndex] = useState(0);
  const [urlArray, setUrlArray] = useState<Array<string>>([]);
  const [noVideo, setNoVideo] = useState(true);
  const [chapterContent, setChapterContent] = useState<Array<any> | undefined>(
    undefined
  );

  const [english, setEnglish] = useState(location.pathname === "/enis");

  useEffect(() => {
    if (location.pathname === "/enis") {
      setEnglish(true);
    } else {
      setEnglish(false);
    }
  }, [setEnglish, location.pathname]);

  const navigate = useNavigate();

  /* load page content and set initial state */
  useEffect(() => {
    if (location.pathname === "/enis") {
      const res = getMembers(db);

      setChapterContent(res);
      const urlArray = getUrlArrayFromChapter(res);
      setUrlArray(urlArray);
      setNoVideo(false);
      return;
    }

    const searchResult = db.filter((a: any) => a.slug === params.slug);
    if (searchResult.length === 0) {
      setChapterContent([]);
      return;
    }

    const res = getMembers(searchResult);
    setChapterContent(res);

    if (searchResult[0].no_video) {
      setUrlArray([]);
      setUrl("");
      setUrlIndex(-1);
    } else {
      setUrlArray(getUrlArrayFromChapter(searchResult));
    }

    if (searchResult[0].no_video) {
      setNoVideo(true);
    } else {
      setNoVideo(false);
    }
  }, [params.slug, location.pathname, db]);

  /* show back to top button on scroll */
  useEffect(() => {
    window.addEventListener("scroll", () => {
      if (window.scrollY > 100) {
        setBackToTop(true);
      } else {
        setBackToTop(false);
      }
    });
  }, []);

  const scrollUp = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* update URL when urlIndex changes */
  useEffect(() => {
    setUrl(urlArray[urlIndex]);
  }, [setUrl, urlIndex, urlArray]);

  const onParagraphClick = (url: string) => {
    if (noVideo) {
      return;
    }

    if (url.length === 0 || url.slice(0, 7) === "NOMATCH") {
      return;
    }

    setUrl(url || ""); /* Set a placeholder instead */
    setPlaying(true);

    /* update URL index when user clicks on a paragraph */
    const foundIndex = urlArray.findIndex((i: string) => i === url);

    if (foundIndex === -1) {
      setUrlIndex(0);
    } else {
      setUrlIndex(foundIndex);
    }
  };

  const createHeadline = (headline: string, number?: string) => {
    if (number) {
      return { __html: number.concat(" ").concat(headline) };
    } else {
      return { __html: headline };
    }
  };

  /* redirect to 404 if no article was found */
  if (chapterContent !== undefined && chapterContent.length === 0) {
    navigate("/404");
    return <></>;
  }

  /* wait until the content loads */
  if (chapterContent === undefined) {
    return <></>;
  }

  const chapterHeadline = (
    <div id={chapterContent[0].number + ". " + chapterContent[0].headline}>
      <h1
        className={clsx(classes.headingOne, [
          !(
            params.slug === "Reference" ||
            params.slug === "Seznam-zkratek" ||
            noVideo
          ) && classes.clickable,
        ])}
        dangerouslySetInnerHTML={createHeadline(
          chapterContent[0].headline,
          chapterContent[0].number
        )}
        onClick={() => {
          onParagraphClick(chapterContent[0].headline_url);
        }}
        style={{
          backgroundColor:
            chapterContent[0].headline_url === url &&
            !(
              params.slug === "Reference" ||
              params.slug === "Seznam-zkratek" ||
              noVideo
            )
              ? "white"
              : undefined,
        }}
      />
    </div>
  );

  return (
    <Wrapper background="rgb(240, 240, 240)">
      <Helmet>
        <title>{chapterContent[0].headline}</title>
      </Helmet>
      <Breadcrumbs />
      <div className={classes.contentWrapper}>
        {chapterHeadline}
        {params.slug === "Reference" ||
        params.slug === "Seznam-zkratek" ||
        params.slug === "seznam-obrazku" ||
        params.slug === "vokabular" ||
        params.slug === "Prehled-tvaru-ruky-ceskeho-znakoveho-jazyka" ||
        params.slug ===
          "Seznam-zkratek-uziti-symbolu-a-poznamek-k-zapisu-znaku" ||
        params.slug === "Seznam-obrazku-a-tabulek" ||
        params.slug === "Seznam-pouzite-literatury" ||
        params.slug === "Seznam-doporucene-literatury-dle-temat" ||
        hideTOC ? null : (
          <TOC
            english={english}
            item={
              location.pathname === "/enis"
                ? db[0]
                : db.filter((a: any) => a.slug === params.slug)[0]
            }
          />
        )}
        <VideoTextContainer
          chapterContent={chapterContent}
          url={url}
          onParagraphClick={onParagraphClick}
          noVideo={noVideo}
          urlArray={urlArray}
          urlIndex={urlIndex}
          setUrlIndex={setUrlIndex}
          playing={playing}
          setPlaying={setPlaying}
          setUrl={setUrl}
          english={english}
        />
        {location.pathname === "/popisy/vokabular" && (
          <Vocabulary
            item={
              db.filter((item: any) => item.slug === "vokabular")[0].vocabulary
            }
          />
        )}

        {!!!hideButtons && (
          <ChapterSwitchButtons
            baselink={baselink}
            db={db}
            slug={params.slug}
            max={db.length - 1}
          />
        )}
      </div>
      {backToTop && (
        <button className={classes.scrollToTop} onClick={scrollUp}>
          <FontAwesomeIcon icon={faArrowUp} />{" "}
          {english ? "Back to top" : "Zpět"}
        </button>
      )}
    </Wrapper>
  );
};

export default Article;
